import React, { useState, useEffect, useRef, useMemo } from 'react';
import { onSnapshot } from 'firebase/firestore';
import type { DocumentReference } from 'firebase/firestore';
import { Modal, Button, ProgressBar, Table, Form, Row, Col, Alert, Collapse, Spinner } from 'react-bootstrap';
import {
  requestProfitPlus2k8Sync,
  setProfitPlus2k8InitialSetupCompleted,
  listProfitPlus2k8Staging,
  promoteProfitPlus2k8StagingViaWebSocket,
  loadConfigurationFactors,
  loadProfitPlus2k8IntegrationConfig,
  updateProfitPlus2k8IntegrationConfig,
  loadProfitPlus2k8ReferenceTables,
  hasPersistedProfitReferenceExportConfig,
  ProfitPlus2k8BridgeStatus,
  ProfitPlus2k8AvailableCounts,
  ProfitPlus2k8StagingItem,
  ProfitPlus2k8PromoteProgress,
} from '../../../api/configuration';
import type { ConfigurationFactors, ProfitPlus2k8OrgConfig, ProfitPlus2k8ReferenceTables } from '../../../models/configuration';
import { DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG } from '../../../models/configuration';

/** MayorX currency code → Profit Plus co_mone (for auto currency mapping). */
const MAYORX_TO_PROFIT_CURRENCY: Record<string, string> = {
  USD: '$',
  USF: '$',
  /** Same catalog row as $ / US$ in dbo.moneda (Dolares Americanos). */
  US$: '$',
  VES: 'BSF',
  BS: 'BS',
  PAB: 'B/.',
};

type SetupStepKey = 'referenceData' | keyof ProfitPlus2k8AvailableCounts;

const REFERENCE_STEP = { key: 'referenceData' as const, label: 'Tablas de referencia' };


const STEPS: { key: SetupStepKey; label: string }[] = [
  REFERENCE_STEP,
  { key: 'salesPeople', label: 'Vendedores' },
  { key: 'clients', label: 'Clientes' },
  { key: 'products', label: 'Productos' },
  // First release: omit sales staging/import step.
  // { key: 'sales', label: 'Ventas' },
];

/** Display fields per entity for the staging table. */
const STAGING_DISPLAY: Record<string, string[]> = {
  // For sales people, show both modeled fields and key native ERP fields
  // profitPlus2k8.fields.* comes from ProfitPlusVendedorRecord in the staging document.
  salesPeople: [
    'code',
    'name',
    'email',
    'mobile',
    'profitPlus2k8.fields.cedula',
    'profitPlus2k8.fields.tipo',
    'profitPlus2k8.fields.telefonos',
    'profitPlus2k8.fields.comision',
    'profitPlus2k8.fields.comisionv',
  ],
  // Clients: modeled fields + resolved lookup names (from JOINs: tipo_cli, zona, segmento, vendedor, cta_ingr)
  clients: [
    'code',
    'businessName',
    'rif',
    'email',
    'fiscalAddress',
    'officePhone',
    'profitPlus2k8.fields.tipo_cliente',
    'profitPlus2k8.fields.zona_des',
    'profitPlus2k8.fields.segmento_des',
    'profitPlus2k8.fields.vendedor_des',
    'profitPlus2k8.fields.plaz_pag',
    'profitPlus2k8.fields.mont_cre',
  ],
  // Products: modeled fields + resolved lookups (lin_des, cat_des, subl_des, des_col, prov_des, etc.)
  products: [
    'code',
    'name',
    'reference',
    'price',
    'profitPlus2k8.fields.lin_des',
    'profitPlus2k8.fields.cat_des',
    'profitPlus2k8.fields.subl_des',
    'profitPlus2k8.fields.des_col',
    'profitPlus2k8.fields.prov_des',
    'profitPlus2k8.fields.des_proc',
    'profitPlus2k8.fields.uni_venta_des',
    'profitPlus2k8.fields.stock_act',
    'profitPlus2k8.fields.stock_min',
    'profitPlus2k8.fields.ubicacion',
    'profitPlus2k8.fields.ref',
    'profitPlus2k8.fields.comentario',
  ],
  // sales: ['saleNumber', 'clientName', 'total'],
};

/** Human-readable labels for staging table columns (key = field path). */
const STAGING_FIELD_LABELS: Record<string, string> = {
  code: 'Código',
  name: 'Nombre',
  email: 'Email',
  mobile: 'Móvil',
  businessName: 'Razón social',
  rif: 'RIF',
  fiscalAddress: 'Dirección fiscal',
  officePhone: 'Teléfono',
  saleNumber: 'Nº venta',
  clientName: 'Cliente',
  total: 'Total',
  price: 'Precio',
  'profitPlus2k8.fields.cedula': 'Cédula',
  'profitPlus2k8.fields.tipo': 'Tipo',
  'profitPlus2k8.fields.telefonos': 'Teléfonos',
  'profitPlus2k8.fields.comision': 'Comisión',
  'profitPlus2k8.fields.comisionv': 'Comisión V',
  'profitPlus2k8.fields.tipo_cliente': 'Tipo cliente',
  'profitPlus2k8.fields.zona_des': 'Zona',
  'profitPlus2k8.fields.segmento_des': 'Segmento',
  'profitPlus2k8.fields.vendedor_des': 'Vendedor',
  'profitPlus2k8.fields.plaz_pag': 'Plazo pago (días)',
  'profitPlus2k8.fields.mont_cre': 'Límite crédito',
  'profitPlus2k8.fields.cta_ingr_des': 'Cuenta ingreso',
  reference: 'Referencia',
  'profitPlus2k8.fields.lin_des': 'Línea',
  'profitPlus2k8.fields.cat_des': 'Categoría',
  'profitPlus2k8.fields.subl_des': 'Sublínea',
  'profitPlus2k8.fields.des_col': 'Color/Marca',
  'profitPlus2k8.fields.prov_des': 'Proveedor',
  'profitPlus2k8.fields.des_proc': 'Procedencia',
  'profitPlus2k8.fields.uni_venta_des': 'Unidad venta',
  'profitPlus2k8.fields.stock_act': 'Stock actual',
  'profitPlus2k8.fields.stock_min': 'Stock mín.',
  'profitPlus2k8.fields.ubicacion': 'Ubicación',
  'profitPlus2k8.fields.ref': 'Ref. fabricante',
  'profitPlus2k8.fields.comentario': 'Comentario',
};

const fieldLabel = (path: string): string => STAGING_FIELD_LABELS[path] ?? path;

/** Initial chunk and increment for «Cargar más» in the staging table (all rows are loaded from the API). */
const STAGING_TABLE_PAGE_SIZE = 200;
/** Server page size for Profit Plus staging list (avoids huge JSON responses). */
const STAGING_API_PAGE_SIZE = 100;

const renderCellValue = (value: unknown): React.ReactNode => {
  if (value == null || value === '') return '—';
  const text = String(value);
  if (text.length <= 32) return text;
  return (
    <span title={text}>
      {text.slice(0, 32)}
      …
    </span>
  );
};

/** Resolve nested field paths like `profitPlus2k8.fields.cedula` against a staging item. */
const resolveFieldValue = (item: ProfitPlus2k8StagingItem, path: string): unknown => {
  const parts = path.split('.');
  let current: any = item;
  for (const part of parts) {
    if (current == null) return undefined;
    current = current[part];
  }
  return current;
};

interface ProfitPlus2k8SetupModalProps {
  show: boolean;
  organizationId: string;
  status: ProfitPlus2k8BridgeStatus;
  onClose: () => void;
}

const ProfitPlus2k8SetupModal: React.FC<ProfitPlus2k8SetupModalProps> = ({
  show,
  organizationId,
  status,
  onClose,
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [phase, setPhase] = useState<'idle' | 'syncing' | 'review'>('idle');
  const [stagingItems, setStagingItems] = useState<ProfitPlus2k8StagingItem[]>([]);
  /** Full staging row count from server (aggregation), may exceed loaded `stagingItems`. */
  const [stagingTotal, setStagingTotal] = useState(0);
  const [stagingNextPageToken, setStagingNextPageToken] = useState<string | null>(null);
  const [stagingServerLoadingMore, setStagingServerLoadingMore] = useState(false);
  const [loadingStaging, setLoadingStaging] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promoteProgress, setPromoteProgress] = useState<ProfitPlus2k8PromoteProgress | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /** How many staging rows to render in the DOM (subset of loaded items). */
  const [stagingVisibleRows, setStagingVisibleRows] = useState(STAGING_TABLE_PAGE_SIZE);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  /** Step active when user clicked Importar (queue doc completion loads staging for this step). */
  const importStepKeyRef = useRef<SetupStepKey | null>(null);
  const [importQueueDocRef, setImportQueueDocRef] = useState<DocumentReference | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const scrollStripRef = useRef<HTMLDivElement | null>(null);
  const isSyncingScrollRef = useRef(false);

  const counts = status.availableCounts || {};
  const step = STEPS[stepIndex];
  const count = step && step.key !== 'referenceData' ? (counts[step.key as keyof ProfitPlus2k8AvailableCounts] ?? 0) : 0;
  const isLastStep = stepIndex >= STEPS.length;
  const isSyncing = phase === 'syncing' || importQueueDocRef !== null;
  const isReferenceStep = step?.key === 'referenceData';
  const [referenceData, setReferenceData] = useState<ProfitPlus2k8ReferenceTables | null>(null);

  const [referenceAlmacen, setReferenceAlmacen] = useState<string>('01');
  /** dbo.sub_alma.co_sub — default row for new product stock (saved in factors). */
  const [referenceDefaultSubAlmacen, setReferenceDefaultSubAlmacen] = useState<string>('');
  const [referenceTaxMapping, setReferenceTaxMapping] = useState<Record<string, string>>({});
  /** MasterProfit.dbo.employee.employee_i — ERP user for all MayorX → Profit exports. */
  const [syncEmployeeI, setSyncEmployeeI] = useState('');
  const [savingReference, setSavingReference] = useState(false);
  const [savedReferenceSuccess, setSavedReferenceSuccess] = useState(false);
  /** Reference step: «Guardar en configuración» completed (or already present in Firestore). */
  const [referenceIntegrationSaved, setReferenceIntegrationSaved] = useState(false);
  const [showAllReferenceData, setShowAllReferenceData] = useState(false);
  const [factorsForReference, setFactorsForReference] = useState<ConfigurationFactors | null>(null);

  /** Block «Siguiente» while Profit import runs, staging reloads, promote to org, or reference save. */
  const blockWizardNext = loadingStaging || promoting || isSyncing || savingReference;

  const referenceSubAlmacenesForAlma = useMemo(() => {
    if (!referenceData?.subAlmacenes?.length) return [];
    const alma = referenceAlmacen.trim();
    return referenceData.subAlmacenes.filter(
      (s: { co_alma?: string }) => String(s.co_alma ?? '').trim() === alma
    );
  }, [referenceData?.subAlmacenes, referenceAlmacen]);

  /** First row from bridge — default ERP user (avoid stale / wrong values from saved config). */
  const firstBridgeEmployeeI = useMemo(() => {
    const list = referenceData?.empleados;
    if (!list?.length) return '';
    const row = list.find((e: { employee_i?: string | number }) =>
      String(e?.employee_i ?? '').trim()
    );
    return row ? String(row.employee_i).trim() : '';
  }, [referenceData?.empleados]);

  const resolvedSyncEmployeeI = useMemo(() => {
    const list = referenceData?.empleados || [];
    const ids = new Set(
      list
        .map((e: { employee_i?: string | number }) => String(e?.employee_i ?? '').trim())
        .filter(Boolean)
    );
    const cur = syncEmployeeI.trim();
    if (cur && ids.has(cur)) return cur;
    return firstBridgeEmployeeI;
  }, [referenceData?.empleados, syncEmployeeI, firstBridgeEmployeeI]);

  useEffect(() => {
    const list = referenceData?.empleados || [];
    const ids = new Set(
      list
        .map((e: { employee_i?: string | number }) => String(e?.employee_i ?? '').trim())
        .filter(Boolean)
    );
    const cur = syncEmployeeI.trim();
    if (cur && !ids.has(cur) && firstBridgeEmployeeI) {
      setSyncEmployeeI(firstBridgeEmployeeI);
    }
  }, [referenceData?.empleados, syncEmployeeI, firstBridgeEmployeeI]);

  useEffect(() => {
    if (!referenceSubAlmacenesForAlma.length) return;
    setReferenceDefaultSubAlmacen(prev => {
      const cur = prev.trim();
      const ok = referenceSubAlmacenesForAlma.some(
        (s: { co_sub?: string }) => String(s.co_sub ?? '').trim() === cur
      );
      if (ok) return prev;
      return String(referenceSubAlmacenesForAlma[0].co_sub ?? '').trim();
    });
  }, [referenceSubAlmacenesForAlma]);

  useEffect(() => {
    if (!show || !organizationId) return;
    let cancelled = false;
    loadProfitPlus2k8IntegrationConfig(organizationId)
      .then(pp => {
        if (!cancelled) setReferenceIntegrationSaved(hasPersistedProfitReferenceExportConfig(pp));
      })
      .catch(() => {
        if (!cancelled) setReferenceIntegrationSaved(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, organizationId]);

  useEffect(() => {
    setStagingVisibleRows(STAGING_TABLE_PAGE_SIZE);
  }, [stepIndex]);

  useEffect(() => {
    if (!loadingStaging) {
      setStagingVisibleRows(STAGING_TABLE_PAGE_SIZE);
    }
  }, [loadingStaging]);

  const REFERENCE_TABLE_KEYS_FOR_MODAL: Array<keyof ProfitPlus2k8ReferenceTables> = useMemo(
    () => [
      'almacenes',
      'subAlmacenes',
      'monedas',
      'condiciones',
      'transportistas',
      'tiposImpuesto',
      'lineas',
      'categorias',
      'sublineas',
      'marcas',
      'procedencias',
      'unidades',
      'proveedores',
      'tiposCliente',
      'zonas',
      'segmentos',
      'vendedoresProfit',
      'cuentasIngreso',
      'empleados',
    ],
    []
  );

  useEffect(() => {
    if (!isReferenceStep || !organizationId) return;
    let cancelled = false;
    (async () => {
      const [f, integrationConfig, refTables] = await Promise.all([
        loadConfigurationFactors(organizationId),
        loadProfitPlus2k8IntegrationConfig(organizationId),
        loadProfitPlus2k8ReferenceTables(organizationId, REFERENCE_TABLE_KEYS_FOR_MODAL),
      ]);
      if (cancelled) return;
      setFactorsForReference(f);
      setReferenceData(refTables);
      const taxMapping = integrationConfig.taxMapping ?? {};
      setReferenceTaxMapping(
        taxMapping && typeof taxMapping === 'object' ? (taxMapping as Record<string, string>) : {}
      );

      const almas = refTables.almacenes || [];
      const savedAlma = (integrationConfig.orderExportAlmacen ?? '').trim();
      if (savedAlma && almas.some((a: { co_alma?: string }) => String(a.co_alma ?? '').trim() === savedAlma)) {
        setReferenceAlmacen(savedAlma);
      } else if (almas.length > 0) {
        setReferenceAlmacen(String(almas[0].co_alma ?? '').trim());
      } else {
        setReferenceAlmacen('');
      }

      const almaFinal = savedAlma && almas.some((a: { co_alma?: string }) => String(a.co_alma ?? '').trim() === savedAlma)
        ? savedAlma
        : almas.length > 0
          ? String(almas[0].co_alma ?? '').trim()
          : '';

      const subsForAlma = (refTables.subAlmacenes || []).filter(
        (s: { co_alma?: string }) => String(s.co_alma ?? '').trim() === almaFinal
      );

      const savedSub = (integrationConfig.defaultProductStockSubAlmacen ?? '').trim();
      if (savedSub && subsForAlma.some((s: { co_sub?: string }) => String(s.co_sub ?? '').trim() === savedSub)) {
        setReferenceDefaultSubAlmacen(savedSub);
      } else if (subsForAlma.length > 0) {
        setReferenceDefaultSubAlmacen(String(subsForAlma[0].co_sub ?? '').trim());
      } else {
        const anySub = (refTables.subAlmacenes || []).find((s: { co_sub?: string }) =>
          String(s.co_sub ?? '').trim()
        );
        setReferenceDefaultSubAlmacen(anySub ? String(anySub.co_sub).trim() : '');
      }
    })().catch(() => {
      if (!cancelled) setFactorsForReference(null);
    });
    return () => {
      cancelled = true;
    };
  }, [isReferenceStep, organizationId, REFERENCE_TABLE_KEYS_FOR_MODAL]);

  // Bridge removes syncQueue doc when import finishes — then load staging / reference rows.
  useEffect(() => {
    if (!importQueueDocRef) return;
    const unsub = onSnapshot(importQueueDocRef, (snap) => {
      if (snap.exists()) return;
      setImportQueueDocRef(null);
      const sk = importStepKeyRef.current;
      importStepKeyRef.current = null;
      setPhase('review');
      if (sk === 'referenceData') {
        setStagingItems([]);
        setStagingTotal(0);
        setStagingNextPageToken(null);
        setSelectedIds(new Set());
        loadProfitPlus2k8ReferenceTables(organizationId, REFERENCE_TABLE_KEYS_FOR_MODAL)
          .then((rt) => setReferenceData(rt))
          .catch(() => {});
        return;
      }
      if (!sk) return;
      setLoadingStaging(true);
      listProfitPlus2k8Staging(organizationId, sk as 'salesPeople' | 'clients' | 'products' | 'sales', {
        limit: STAGING_API_PAGE_SIZE,
      })
        .then((res) => {
          setStagingItems(res.items || []);
          setStagingTotal(typeof res.total === 'number' ? res.total : (res.items || []).length);
          setStagingNextPageToken(res.nextPageToken ?? null);
          setSelectedIds(new Set((res.items || []).map((i) => i.id)));
        })
        .catch((err) => {
          console.error('Failed to load staging:', err);
          setStagingItems([]);
          setStagingTotal(0);
          setStagingNextPageToken(null);
          setSelectedIds(new Set());
        })
        .finally(() => setLoadingStaging(false));
    });
    return () => unsub();
  }, [importQueueDocRef, organizationId, REFERENCE_TABLE_KEYS_FOR_MODAL]);

  // Reset phase when changing step (cancel local tracking of a queued import job)
  useEffect(() => {
    setImportQueueDocRef(null);
    importStepKeyRef.current = null;
    setPhase('idle');
    setStagingItems([]);
    setStagingTotal(0);
    setStagingNextPageToken(null);
    setSelectedIds(new Set());
  }, [stepIndex]);

  const handleImport = async () => {
    if (!step || isSyncing) return;
    setPhase('syncing');
    const entity = step.key as 'referenceData' | 'salesPeople' | 'clients' | 'products' | 'sales';
    importStepKeyRef.current = step.key;
    try {
      const ref = await requestProfitPlus2k8Sync(organizationId, entity);
      setImportQueueDocRef(ref);
    } catch (e) {
      console.error('Failed to enqueue Profit import sync', e);
      importStepKeyRef.current = null;
      setPhase('idle');
    }
  };

  const handleSaveReferenceConfig = async () => {
    if (!referenceData || savingReference) return;
    setSavingReference(true);
    setSavedReferenceSuccess(false);
    try {
      const factors = await loadConfigurationFactors(organizationId);
      const existingIntegrationConfig = await loadProfitPlus2k8IntegrationConfig(organizationId);
      const refCurrency = (factors.referenceCurrency || 'USD').trim();
      const orgCurrency = (factors.currency || 'VES').trim();
      const orderExportCurrencyMapping: Record<string, string> = {};
      if (refCurrency) orderExportCurrencyMapping[refCurrency] = MAYORX_TO_PROFIT_CURRENCY[refCurrency] ?? referenceData.monedas?.[0]?.co_mone ?? '$';
      if (orgCurrency && orgCurrency !== refCurrency) {
        orderExportCurrencyMapping[orgCurrency] =
          MAYORX_TO_PROFIT_CURRENCY[orgCurrency] ?? '$';
      }
      // Prefer reference currency (often USD → $) so default export moneda is dollars, not org VES→BSF.
      const defaultMoneda =
        orderExportCurrencyMapping[refCurrency] ||
        orderExportCurrencyMapping[orgCurrency] ||
        '$';

      const profitPlus2k8: ProfitPlus2k8OrgConfig = {
        ...existingIntegrationConfig,
        orderExportAlmacen: referenceAlmacen || '01',
        defaultProductStockSubAlmacen: referenceDefaultSubAlmacen.trim() || undefined,
        orderExportCurrencyMapping,
        orderExportDefaultMoneda: defaultMoneda,
        orderExportDefaultTasa: factors.exchangeRate ?? 1,
        taxMapping: Object.keys(referenceTaxMapping).length ? referenceTaxMapping : undefined,
        syncEmployeeI: resolvedSyncEmployeeI || undefined,
      };
      await updateProfitPlus2k8IntegrationConfig(organizationId, profitPlus2k8);
      await setProfitPlus2k8InitialSetupCompleted(organizationId);
      setReferenceIntegrationSaved(true);
      setSavedReferenceSuccess(true);
      setPhase('idle');
      setStepIndex((i) => Math.min(i + 1, STEPS.length));
      setTimeout(() => setSavedReferenceSuccess(false), 4000);
    } catch (e) {
      console.error('Failed to save reference config', e);
    } finally {
      setSavingReference(false);
    }
  };

  const canAdvanceFromReferenceStep = referenceIntegrationSaved;

  const handleSkip = () => {
    if (isLastStep) return;
    if (stepIndex === 0 && !canAdvanceFromReferenceStep) return;
    setStepIndex((i) => Math.min(i + 1, STEPS.length));
  };

  const handleReloadStaging = () => {
    if (!step) return;
    setLoadingStaging(true);
    listProfitPlus2k8Staging(organizationId, step.key as 'salesPeople' | 'clients' | 'products' | 'sales', {
      limit: STAGING_API_PAGE_SIZE,
    })
      .then((res) => {
        setStagingItems(res.items || []);
        setStagingTotal(typeof res.total === 'number' ? res.total : (res.items || []).length);
        setStagingNextPageToken(res.nextPageToken ?? null);
        setSelectedIds(new Set((res.items || []).map((i) => i.id)));
      })
      .catch((err) => {
        console.error('Failed to load staging:', err);
        setStagingItems([]);
        setStagingTotal(0);
        setStagingNextPageToken(null);
        setSelectedIds(new Set());
      })
      .finally(() => setLoadingStaging(false));
  };

  const handleImportSelected = async () => {
    if (!step || promoting || selectedIds.size === 0) return;
    setPromoting(true);
    setPromoteProgress({
      processed: 0,
      total: selectedIds.size,
      promoted: 0,
      errors: 0,
    });
    try {
      const entity = step.key as 'salesPeople' | 'clients' | 'products' | 'sales';
      await promoteProfitPlus2k8StagingViaWebSocket(
        organizationId,
        entity,
        Array.from(selectedIds),
        (p) => setPromoteProgress(p)
      );
      if (isLastStep) {
        setPhase('idle');
        setStepIndex(STEPS.length);
      } else {
        setStepIndex((i) => Math.min(i + 1, STEPS.length));
      }
    } catch (err) {
      console.error('Promote failed:', err);
    } finally {
      setPromoting(false);
      setPromoteProgress(null);
    }
  };

  const handleImportAllStaging = async () => {
    if (!step || promoting || stagingTotal <= 0) return;
    setPromoting(true);
    setPromoteProgress({
      processed: 0,
      total: stagingTotal,
      promoted: 0,
      errors: 0,
    });
    try {
      const entity = step.key as 'salesPeople' | 'clients' | 'products' | 'sales';
      await promoteProfitPlus2k8StagingViaWebSocket(
        organizationId,
        entity,
        [],
        (p) => setPromoteProgress(p),
        { promoteAll: true }
      );
      if (isLastStep) {
        setPhase('idle');
        setStepIndex(STEPS.length);
      } else {
        setStepIndex((i) => Math.min(i + 1, STEPS.length));
      }
    } catch (err) {
      console.error('Promote all failed:', err);
    } finally {
      setPromoting(false);
      setPromoteProgress(null);
    }
  };

  const handleLoadMoreStagingFromServer = () => {
    if (!step || !stagingNextPageToken || stagingServerLoadingMore) return;
    setStagingServerLoadingMore(true);
    listProfitPlus2k8Staging(organizationId, step.key as 'salesPeople' | 'clients' | 'products' | 'sales', {
      limit: STAGING_API_PAGE_SIZE,
      pageToken: stagingNextPageToken,
    })
      .then((res) => {
        setStagingItems((prev) => {
          const seen = new Set(prev.map((i) => i.id));
          const merged = [...prev];
          for (const it of res.items || []) {
            if (!seen.has(it.id)) {
              seen.add(it.id);
              merged.push(it);
            }
          }
          return merged;
        });
        setStagingNextPageToken(res.nextPageToken ?? null);
        if (typeof res.total === 'number') {
          setStagingTotal(res.total);
        }
      })
      .catch((err) => {
        console.error('Failed to load more staging:', err);
      })
      .finally(() => setStagingServerLoadingMore(false));
  };

  const handleNext = () => {
    if (isLastStep) return;
    if (stepIndex === 0 && !canAdvanceFromReferenceStep) return;
    setStepIndex((i) => Math.min(i + 1, STEPS.length));
  };

  const handleFinish = () => {
    onClose();
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === stagingItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(stagingItems.map((i) => i.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const displayFields = step ? (STAGING_DISPLAY[step.key] || ['id']) : ['id'];
  const selectionDisabled = loadingStaging || promoting;

  // Measure table content width so the horizontal scroll strip matches; sync scroll between strip and table
  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;
    const updateWidth = () => setTableScrollWidth(wrapper.scrollWidth);
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [phase, stagingItems.length, stagingVisibleRows, displayFields.length]);

  const handleStripScroll = () => {
    if (isSyncingScrollRef.current) return;
    const strip = scrollStripRef.current;
    const wrapper = tableWrapperRef.current;
    if (strip && wrapper) {
      isSyncingScrollRef.current = true;
      wrapper.scrollLeft = strip.scrollLeft;
      requestAnimationFrame(() => { isSyncingScrollRef.current = false; });
    }
  };

  const handleTableScroll = () => {
    if (isSyncingScrollRef.current) return;
    const strip = scrollStripRef.current;
    const wrapper = tableWrapperRef.current;
    if (strip && wrapper) {
      isSyncingScrollRef.current = true;
      strip.scrollLeft = wrapper.scrollLeft;
      requestAnimationFrame(() => { isSyncingScrollRef.current = false; });
    }
  };

  if (!show) return null;

  return (
    <Modal
      show={show}
      onHide={onClose}
      backdrop={true}
      keyboard
      size="xl"
      centered
      className="profit-plus-setup-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Configuración inicial · Profit Plus 2k8</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          El admin controla el puente: primero se importan las tablas de referencia (almacenes, monedas, impuestos) y se guardan en la configuración; luego se traen vendedores, clientes y productos a un área de revisión.
        </p>
        {!isLastStep && step && isReferenceStep && (
          <>
            <div className="mb-3">
              <strong>{step.label}</strong>
              <span className="text-muted ms-2">Almacenes, monedas, condiciones de pago, tipos de impuesto en Profit</span>
            </div>
            {phase === 'syncing' && step?.key === 'referenceData' && (
              <Alert variant="info" className="mb-3 py-3 border-2 shadow-sm">
                <div className="d-flex align-items-start gap-3">
                  <Spinner animation="border" role="status" className="flex-shrink-0 mt-1" aria-hidden />
                  <div className="flex-grow-1">
                    <div className="fw-semibold mb-2">Sincronizando tablas de referencia</div>
                    <ProgressBar animated now={100} striped variant="primary" className="mb-2" style={{ height: 10 }} />
                    <small className="text-muted">El puente está leyendo Profit y escribiendo datos en revisión. Puede tardar varios minutos.</small>
                  </div>
                </div>
              </Alert>
            )}
            {phase === 'idle' && (
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Button variant="primary" size="sm" onClick={handleImport} disabled={isSyncing}>
                  Importar desde Profit
                </Button>
                {canAdvanceFromReferenceStep && (
                  <>
                    <Button variant="outline-secondary" size="sm" onClick={handleSkip}>
                      Omitir
                    </Button>
                    {stepIndex > 0 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        onClick={handleNext}
                        disabled={blockWizardNext}
                        title={blockWizardNext ? 'Espera a que termine la carga o la importación' : undefined}
                      >
                        Siguiente →
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
            {phase === 'review' && referenceData && (
              <div className="mb-3">
                <p className="small text-muted mb-2">
                  Guarda estas tablas en la configuración de la integración. Las monedas se mapean automáticamente (p. ej. USD→$, VES→BSF). La moneda por defecto en Profit para pedidos sin coincidencia es dólares (<code className="small">$</code> en dbo.moneda). Se usará la tasa de cambio actual de MayorX al exportar pedidos.
                </p>
                <div className="mb-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowAllReferenceData((v) => !v)}
                    aria-expanded={showAllReferenceData}
                  >
                    {showAllReferenceData ? 'Ocultar' : 'Ver'} todos los datos importados
                  </Button>
                  <Collapse in={showAllReferenceData}>
                    <div className="mt-2 small">
                      {(referenceData.almacenes?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <strong>Almacenes</strong> ({referenceData.almacenes!.length})
                          <Table size="sm" bordered striped className="mb-0 mt-1">
                            <thead><tr><th>Código</th><th>Descripción</th></tr></thead>
                            <tbody>
                              {referenceData.almacenes!.slice(0, 50).map((a: any, i: number) => (
                                <tr key={i}><td>{String(a.co_alma ?? '').trim()}</td><td>{String(a.alma_des ?? '').trim()}</td></tr>
                              ))}
                              {(referenceData.almacenes!.length ?? 0) > 50 && <tr><td colSpan={2} className="text-muted">… y {referenceData.almacenes!.length! - 50} más</td></tr>}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {(referenceData.subAlmacenes?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <strong>Sub-almacenes</strong> ({referenceData.subAlmacenes!.length})
                          <Table size="sm" bordered striped className="mb-0 mt-1">
                            <thead><tr><th>Almacén</th><th>Sub</th><th>Descripción</th></tr></thead>
                            <tbody>
                              {referenceData.subAlmacenes!.slice(0, 50).map((s: any, i: number) => (
                                <tr key={i}><td>{String(s.co_alma ?? '').trim()}</td><td>{String(s.co_sub ?? '').trim()}</td><td>{String(s.des_sub ?? '').trim()}</td></tr>
                              ))}
                              {(referenceData.subAlmacenes!.length ?? 0) > 50 && <tr><td colSpan={3} className="text-muted">… y {referenceData.subAlmacenes!.length! - 50} más</td></tr>}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {(referenceData.monedas?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <strong>Monedas</strong> ({referenceData.monedas!.length})
                          <Table size="sm" bordered striped className="mb-0 mt-1">
                            <thead><tr><th>Código</th><th>Descripción</th><th>Cambio</th></tr></thead>
                            <tbody>
                              {referenceData.monedas!.map((m: any, i: number) => (
                                <tr key={i}><td>{String(m.co_mone ?? '').trim()}</td><td>{String(m.mone_des ?? '').trim()}</td><td>{m.cambio != null ? Number(m.cambio) : '—'}</td></tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {(referenceData.condiciones?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <strong>Condiciones de pago</strong> ({referenceData.condiciones!.length})
                          <Table size="sm" bordered striped className="mb-0 mt-1">
                            <thead><tr><th>Código</th><th>Descripción</th><th>Días créd.</th></tr></thead>
                            <tbody>
                              {referenceData.condiciones!.slice(0, 30).map((c: any, i: number) => (
                                <tr key={i}><td>{String(c.co_cond ?? '').trim()}</td><td>{String(c.cond_des ?? '').trim()}</td><td>{c.dias_cred ?? '—'}</td></tr>
                              ))}
                              {(referenceData.condiciones!.length ?? 0) > 30 && <tr><td colSpan={3} className="text-muted">… y {referenceData.condiciones!.length! - 30} más</td></tr>}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {(referenceData.transportistas?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <strong>Transportistas (pedidos.co_tran)</strong> ({referenceData.transportistas!.length})
                          <Table size="sm" bordered striped className="mb-0 mt-1">
                            <thead><tr><th>Código</th><th>Descripción</th></tr></thead>
                            <tbody>
                              {referenceData.transportistas!.slice(0, 30).map((t: any, i: number) => (
                                <tr key={i}><td>{String(t.co_tran ?? '').trim()}</td><td>{String(t.des_tran ?? '').trim()}</td></tr>
                              ))}
                              {(referenceData.transportistas!.length ?? 0) > 30 && <tr><td colSpan={2} className="text-muted">… y {referenceData.transportistas!.length! - 30} más</td></tr>}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {(referenceData.tiposImpuesto?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <strong>Tipos de impuesto (Profit)</strong> ({referenceData.tiposImpuesto!.length})
                          <Table size="sm" bordered striped className="mb-0 mt-1">
                            <thead><tr><th>Tipo</th><th>Descripción</th><th>%</th></tr></thead>
                            <tbody>
                              {referenceData.tiposImpuesto!.map((t: any, i: number) => (
                                <tr key={i}><td>{String(t.tipo ?? '').trim()}</td><td>{String(t.descripcio ?? '').trim()}</td><td>{t.porc_vent != null ? Number(t.porc_vent) : '—'}</td></tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {(referenceData.lineas?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <strong>Líneas de artículo</strong> ({referenceData.lineas!.length})
                          <Table size="sm" bordered striped className="mb-0 mt-1">
                            <thead><tr><th>Código</th><th>Descripción</th></tr></thead>
                            <tbody>
                              {referenceData.lineas!.slice(0, 50).map((l: any, i: number) => (
                                <tr key={i}><td>{String(l.co_lin ?? '').trim()}</td><td>{String(l.lin_des ?? '').trim()}</td></tr>
                              ))}
                              {(referenceData.lineas!.length ?? 0) > 50 && <tr><td colSpan={2} className="text-muted">… y {referenceData.lineas!.length! - 50} más</td></tr>}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {(referenceData.categorias?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <strong>Categorías</strong> ({referenceData.categorias!.length})
                          <Table size="sm" bordered striped className="mb-0 mt-1">
                            <thead><tr><th>Código</th><th>Descripción</th></tr></thead>
                            <tbody>
                              {referenceData.categorias!.slice(0, 50).map((c: any, i: number) => (
                                <tr key={i}><td>{String(c.co_cat ?? '').trim()}</td><td>{String(c.cat_des ?? '').trim()}</td></tr>
                              ))}
                              {(referenceData.categorias!.length ?? 0) > 50 && <tr><td colSpan={2} className="text-muted">… y {referenceData.categorias!.length! - 50} más</td></tr>}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {(referenceData.sublineas?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <strong>Sublíneas</strong> ({referenceData.sublineas!.length})
                          <Table size="sm" bordered striped className="mb-0 mt-1">
                            <thead><tr><th>Sublínea</th><th>Línea</th><th>Descripción</th></tr></thead>
                            <tbody>
                              {referenceData.sublineas!.slice(0, 50).map((s: any, i: number) => (
                                <tr key={i}><td>{String(s.co_subl ?? '').trim()}</td><td>{String(s.co_lin ?? '').trim()}</td><td>{String(s.subl_des ?? '').trim()}</td></tr>
                              ))}
                              {(referenceData.sublineas!.length ?? 0) > 50 && <tr><td colSpan={3} className="text-muted">… y {referenceData.sublineas!.length! - 50} más</td></tr>}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {(referenceData.marcas?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <strong>Marcas / Colores</strong> ({referenceData.marcas!.length})
                          <Table size="sm" bordered striped className="mb-0 mt-1">
                            <thead><tr><th>Código</th><th>Descripción</th></tr></thead>
                            <tbody>
                              {referenceData.marcas!.slice(0, 50).map((m: any, i: number) => (
                                <tr key={i}><td>{String(m.co_col ?? '').trim()}</td><td>{String(m.des_col ?? '').trim()}</td></tr>
                              ))}
                              {(referenceData.marcas!.length ?? 0) > 50 && <tr><td colSpan={2} className="text-muted">… y {referenceData.marcas!.length! - 50} más</td></tr>}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {(referenceData.procedencias?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <strong>Procedencias</strong> ({referenceData.procedencias!.length})
                          <Table size="sm" bordered striped className="mb-0 mt-1">
                            <thead><tr><th>Código</th><th>Descripción</th></tr></thead>
                            <tbody>
                              {referenceData.procedencias!.map((p: any, i: number) => (
                                <tr key={i}><td>{String(p.cod_proc ?? '').trim()}</td><td>{String(p.des_proc ?? '').trim()}</td></tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {(referenceData.unidades?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <strong>Unidades de medida</strong> ({referenceData.unidades!.length})
                          <Table size="sm" bordered striped className="mb-0 mt-1">
                            <thead><tr><th>Código</th><th>Descripción</th></tr></thead>
                            <tbody>
                              {referenceData.unidades!.map((u: any, i: number) => (
                                <tr key={i}><td>{String(u.co_uni ?? '').trim()}</td><td>{String(u.des_uni ?? '').trim()}</td></tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {(referenceData.proveedores?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <strong>Proveedores</strong> ({referenceData.proveedores!.length})
                          <Table size="sm" bordered striped className="mb-0 mt-1">
                            <thead><tr><th>Código</th><th>Descripción</th></tr></thead>
                            <tbody>
                              {referenceData.proveedores!.slice(0, 50).map((p: any, i: number) => (
                                <tr key={i}><td>{String(p.co_prov ?? '').trim()}</td><td>{String(p.prov_des ?? '').trim()}</td></tr>
                              ))}
                              {(referenceData.proveedores!.length ?? 0) > 50 && <tr><td colSpan={2} className="text-muted">… y {referenceData.proveedores!.length! - 50} más</td></tr>}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {!(referenceData.almacenes?.length || referenceData.monedas?.length || referenceData.lineas?.length) && (
                        <p className="text-muted mb-0">No hay datos de referencia importados.</p>
                      )}
                    </div>
                  </Collapse>
                </div>
                <Row className="g-2 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="small">Almacén / sucursal (pedidos)</Form.Label>
                      <Form.Select
                        size="sm"
                        value={referenceAlmacen}
                        onChange={(e) => {
                          const nextAlma = e.target.value;
                          setReferenceAlmacen(nextAlma);
                          const subs = (referenceData.subAlmacenes || []).filter(
                            (s: { co_alma?: string }) =>
                              String(s.co_alma ?? '').trim() === nextAlma.trim()
                          );
                          const cur = referenceDefaultSubAlmacen.trim();
                          const ok = subs.some(
                            (s: { co_sub?: string }) => String(s.co_sub ?? '').trim() === cur
                          );
                          if (!ok && subs.length) {
                            setReferenceDefaultSubAlmacen(String(subs[0].co_sub ?? '').trim());
                          }
                        }}
                      >
                        {(referenceData.almacenes || []).map((a: { co_alma?: string; alma_des?: string }) => (
                          <option key={String(a.co_alma)} value={String(a.co_alma ?? '').trim() || '01'}>
                            {String(a.co_alma ?? '').trim()} — {String(a.alma_des ?? '').trim() || '—'}
                          </option>
                        ))}
                        {(!referenceData.almacenes || referenceData.almacenes.length === 0) && (
                          <option value="01">01</option>
                        )}
                      </Form.Select>
                      <Form.Text className="text-muted small">
                        Sucursal usada al exportar pedidos. Debe tener al menos un sub-almacén en Profit (dbo.sub_alma).
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="small">Sub-almacén por defecto (inventario nuevo)</Form.Label>
                      <Form.Select
                        size="sm"
                        value={referenceDefaultSubAlmacen}
                        onChange={(e) => setReferenceDefaultSubAlmacen(e.target.value)}
                        disabled={referenceSubAlmacenesForAlma.length === 0}
                      >
                        {referenceSubAlmacenesForAlma.map((s: { co_sub?: string; des_sub?: string }, i: number) => {
                          const sub = String(s.co_sub ?? '').trim();
                          if (!sub) return null;
                          return (
                            <option key={`${sub}-${i}`} value={sub}>
                              {sub} — {String(s.des_sub ?? '').trim() || '—'}
                            </option>
                          );
                        })}
                      </Form.Select>
                      <Form.Text className="text-muted small">
                        Donde se registra el stock al crear un producto en MayorX (dbo.st_almac) hasta que repartas en otros.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <div className="mb-3">
                  <strong className="small d-block mb-2">Usuario ERP en sincronización (MasterProfit.dbo.employee)</strong>
                  <p className="text-muted small mb-2">
                    El valor guardado es <code className="small">employee_i</code> de{' '}
                    <code className="small">[MasterProfit].[dbo].[employee]</code>. Por defecto se toma el{' '}
                    <strong>primer empleado</strong> devuelto por el puente; puede cambiarlo si lo necesita.
                  </p>
                  {(!(referenceData.empleados?.length ?? 0)) && (
                    <Alert variant="warning" className="py-2 small mb-2">
                      No hay empleados en los datos de referencia. Vuelva a importar tablas de referencia desde Profit para cargar{' '}
                      <code className="small">[MasterProfit].[dbo].[employee]</code>.
                    </Alert>
                  )}
                  <Row className="g-2">
                    <Col md={8} lg={6}>
                      <Form.Group>
                        <Form.Label className="small">Usuario ERP (exportaciones a Profit)</Form.Label>
                        <Form.Select
                          size="sm"
                          value={resolvedSyncEmployeeI}
                          onChange={(e) => setSyncEmployeeI(e.target.value)}
                          disabled={!(referenceData.empleados?.length ?? 0)}
                        >
                          {(referenceData.empleados || []).map((emp: { employee_i?: string | number; last_name?: string }, i: number) => {
                            const v = String(emp.employee_i ?? '').trim();
                            if (!v) return null;
                            return (
                              <option key={`emp-${v}-${i}`} value={v}>
                                {v} — {String(emp.last_name ?? '').trim() || '—'}
                              </option>
                            );
                          })}
                        </Form.Select>
                        <Form.Text className="text-muted small">
                          Mismo <code className="small">employee_i</code> para clientes, artículos y pedidos exportados desde MayorX. Sin datos de referencia no se puede exportar con un usuario válido.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
                {factorsForReference?.taxes && factorsForReference.taxes.length > 0 && (referenceData.tiposImpuesto?.length ?? 0) > 0 && (
                  <div className="mb-3">
                    <Form.Label className="small">Impuestos MayorX → Profit (tipo_imp)</Form.Label>
                    <Table size="sm" bordered className="mb-2">
                      <thead><tr><th>Impuesto MayorX</th><th>Profit (tipo)</th></tr></thead>
                      <tbody>
                        {factorsForReference.taxes.map((t) => (
                          <tr key={t.id}>
                            <td>{t.name} ({t.rate}%)</td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={referenceTaxMapping[t.id] ?? ''}
                                onChange={(e) => setReferenceTaxMapping((prev) => ({ ...prev, [t.id]: e.target.value }))}
                              >
                                <option value="">— usar del artículo en Profit —</option>
                                {(referenceData.tiposImpuesto || []).map((tip: { tipo?: string; descripcio?: string; porc_vent?: number }) => (
                                  <option key={String(tip.tipo)} value={String(tip.tipo ?? '').trim()}>
                                    {String(tip.tipo ?? '').trim()} — {String(tip.descripcio ?? '').trim()} ({Number(tip.porc_vent ?? 0)}%)
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
                {savedReferenceSuccess && (
                  <Alert variant="success" className="py-2 mb-2 small">
                    ✓ Tablas de referencia guardadas en la configuración. Ya puedes usar los desplegables en Inventario.
                  </Alert>
                )}
                {!referenceIntegrationSaved && (
                  <Alert variant="warning" className="py-2 small mb-2">
                    Guarde la configuración con <strong>Guardar en configuración</strong> antes de continuar. Es
                    necesaria para exportaciones y desplegables de la integración.
                  </Alert>
                )}
                <div className="d-flex gap-2 align-items-center flex-wrap">
                  <Button variant="primary" size="sm" onClick={handleSaveReferenceConfig} disabled={savingReference}>
                    {savingReference ? 'Guardando…' : savedReferenceSuccess ? '✓ Guardado' : 'Guardar en configuración'}
                  </Button>
                  {canAdvanceFromReferenceStep && (
                    <>
                      <Button variant="outline-secondary" size="sm" onClick={handleSkip}>
                        Omitir
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        onClick={handleNext}
                        disabled={blockWizardNext}
                        title={blockWizardNext ? 'Espera a que termine la carga o la importación' : undefined}
                      >
                        Siguiente →
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
        {!isLastStep && step && !isReferenceStep && (
          <>
            <div className="mb-3">
              <strong>{step.label}</strong>
              <span className="text-muted ms-2">
                {count} disponible{count !== 1 ? 's' : ''} en Profit
              </span>
            </div>
            {phase === 'syncing' && step && step.key !== 'referenceData' && (
              <Alert variant="info" className="mb-3 py-3 border-2 shadow-sm">
                <div className="d-flex align-items-start gap-3">
                  <Spinner animation="border" role="status" className="flex-shrink-0 mt-1" aria-hidden />
                  <div className="flex-grow-1">
                    <div className="fw-semibold mb-2">Trayendo {step.label.toLowerCase()} desde Profit</div>
                    <ProgressBar animated now={100} striped variant="primary" className="mb-2" style={{ height: 10 }} />
                    <small className="text-muted">Los datos aparecerán abajo en cuanto el puente termine. No cierres esta ventana.</small>
                  </div>
                </div>
              </Alert>
            )}
            {phase === 'idle' && (
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleImport}
                  disabled={count === 0 || isSyncing}
                >
                  Traer a revisión
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={handleSkip}>
                  Omitir
                </Button>
                {stepIndex > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={handleNext}
                    disabled={blockWizardNext}
                    title={blockWizardNext ? 'Espera a que termine la carga o la importación' : undefined}
                  >
                    Siguiente →
                  </Button>
                )}
              </div>
            )}
            {phase === 'review' && (
              <div className="mb-3">
                {loadingStaging ? (
                  <div className="text-muted small">Cargando datos en revisión…</div>
                ) : stagingItems.length === 0 ? (
                  <div className="text-muted small">No hay datos en revisión. Puedes omitir o traer de nuevo.</div>
                ) : (
                  <>
                    <div className="small text-muted mb-2">
                      Elige qué registros importar (se importarán a tu organización).
                    </div>
                    {promoting && (
                      <Alert variant="primary" className="mb-3 py-3 text-white border-0 shadow">
                        <div className="d-flex align-items-start gap-3">
                          <Spinner animation="border" size="sm" role="status" className="flex-shrink-0 mt-1 border-light" />
                          <div className="flex-grow-1 w-100">
                            <div className="fw-semibold mb-2">Importando a tu organización</div>
                            {promoteProgress && promoteProgress.total > 0 ? (
                              <>
                                <ProgressBar
                                  now={
                                    promoteProgress.total > 0
                                      ? (100 * promoteProgress.processed) / promoteProgress.total
                                      : 0
                                  }
                                  striped
                                  animated
                                  variant="light"
                                  className="mb-2"
                                  style={{ height: 22 }}
                                  label={`${promoteProgress.processed} / ${promoteProgress.total}`}
                                />
                                <div className="small opacity-90">
                                  <span className="me-2">
                                    OK: <strong>{promoteProgress.promoted}</strong>
                                  </span>
                                  {promoteProgress.errors > 0 ? (
                                    <span className="text-warning">
                                      Errores: <strong>{promoteProgress.errors}</strong>
                                    </span>
                                  ) : (
                                    <span className="opacity-75">Errores: 0</span>
                                  )}
                                </div>
                              </>
                            ) : (
                              <div className="small opacity-90">Conectando con el servidor…</div>
                            )}
                          </div>
                        </div>
                      </Alert>
                    )}
                    {/* Horizontal scroll strip so user can scroll columns without scrolling to bottom */}
                    {tableScrollWidth > 0 && (
                      <div
                        ref={scrollStripRef}
                        className="border border-bottom-0 rounded-top-3 overflow-x-auto overflow-y-hidden mb-0"
                        style={{ height: 14, minHeight: 14 }}
                        onScroll={handleStripScroll}
                        aria-hidden
                      >
                        <div style={{ width: tableScrollWidth, minHeight: 1 }} />
                      </div>
                    )}
                    <div
                      ref={tableWrapperRef}
                      className="border rounded-3 overflow-auto mb-2"
                      style={{ maxHeight: 520, ...(tableScrollWidth > 0 ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 } : {}) }}
                      onScroll={handleTableScroll}
                    >
                      <Table
                        size="sm"
                        striped
                        hover
                        className="mb-0 align-middle text-nowrap"
                        style={{ minWidth: 'max-content' }}
                      >
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 40 }}>
                              <Form.Check
                                type="checkbox"
                                checked={selectedIds.size === stagingItems.length && stagingItems.length > 0}
                                onChange={toggleSelectAll}
                                disabled={selectionDisabled}
                              />
                            </th>
                            {displayFields.map((f) => (
                              <th key={f}>{fieldLabel(f)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {stagingItems.slice(0, stagingVisibleRows).map((item) => (
                            <tr key={item.id}>
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  checked={selectedIds.has(item.id)}
                                  onChange={() => toggleSelect(item.id)}
                                  disabled={selectionDisabled}
                                />
                              </td>
                              {displayFields.map((field) => {
                                const value = resolveFieldValue(item, field);
                                return (
                                  <td
                                    key={field}
                                    className="text-truncate"
                                    style={{ maxWidth: 200 }}
                                  >
                                    {renderCellValue(value)}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <div className="small text-muted mb-2">
                      Mostrando {Math.min(stagingVisibleRows, stagingItems.length)} de {stagingItems.length} filas cargadas
                      {stagingTotal > stagingItems.length
                        ? ` (${stagingTotal} en revisión en el servidor).`
                        : '.'}{' '}
                      «Importar seleccionado(s)» solo aplica a los marcados. «Importar todo en revisión» importa la cola completa en el servidor (incluye filas que aún no trajiste a esta tabla).
                    </div>
                    {stagingNextPageToken && (
                      <div className="mb-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleLoadMoreStagingFromServer}
                          disabled={stagingServerLoadingMore || promoting || loadingStaging}
                        >
                          {stagingServerLoadingMore ? 'Cargando…' : 'Traer más del servidor'}
                        </Button>
                      </div>
                    )}
                    {stagingVisibleRows < stagingItems.length && (
                      <div className="mb-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            setStagingVisibleRows((n) =>
                              Math.min(n + STAGING_TABLE_PAGE_SIZE, stagingItems.length)
                            )
                          }
                        >
                          Cargar más ({Math.min(stagingVisibleRows, stagingItems.length)} / {stagingItems.length})
                        </Button>
                      </div>
                    )}
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleImportSelected}
                        disabled={selectedIds.size === 0 || promoting}
                      >
                        {promoting ? 'Importando…' : `Importar ${selectedIds.size} seleccionado(s)`}
                      </Button>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleImportAllStaging}
                        disabled={promoting || loadingStaging || stagingTotal <= 0}
                        title="Importa todos los documentos en revisión en Firestore, no solo los visibles en esta tabla"
                      >
                        {promoting ? 'Importando…' : `Importar todo en revisión (${stagingTotal})`}
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={handleSkip}>
                        Omitir
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleReloadStaging}
                        disabled={loadingStaging}
                      >
                        {loadingStaging ? 'Actualizando…' : 'Reintentar carga'}
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        onClick={handleNext}
                        disabled={blockWizardNext}
                        title={blockWizardNext ? 'Espera a que termine la carga o la importación' : undefined}
                      >
                        Siguiente →
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
        {isLastStep && (
          <div>
            <p className="mb-2">Configuración inicial lista.</p>
            <Button variant="primary" onClick={handleFinish}>
              Cerrar
            </Button>
          </div>
        )}
        <div className="mt-3">
          <ProgressBar
            now={((stepIndex + (isLastStep ? 1 : 0)) / (STEPS.length + 1)) * 100}
            style={{ height: 4 }}
            variant="secondary"
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ProfitPlus2k8SetupModal;
